/**
 * Centralized path constants for admin navigation.
 *
 * These route templates are shared across side-nav and org-switcher configs
 * to maintain a single source of truth. The `:thirdPartySlug` placeholder is
 * substituted at runtime by consumers via `formatRoute()`.
 *
 * Organization structure:
 * - ORG_PATH: Base organization route
 * - BASE_PATH: Base admin path for the organization
 * - Individual feature paths built from BASE_PATH
 */

export const ORG_PATH = '/o/:thirdPartySlug';
export const BASE_PATH = `${ORG_PATH}/admin`;

// Main navigation paths
export const HOME_PATH = `${BASE_PATH}/home`;
export const PROGRAMS_PATH = `${BASE_PATH}/programs`;
export const CONTRACTS_PATH = `${BASE_PATH}/programs/contracts`;
export const LEARNERS_PATH = `${BASE_PATH}/learners`;
export const GROUPS_PATH = `${BASE_PATH}/groups`;
export const CATALOG_PATH = `${BASE_PATH}/catalog`;
export const CURATED_CATALOG_PATH = `${CATALOG_PATH}/curated-catalog`;
export const ASSIGNED_CONTENT_CATALOG_PATH = `${CATALOG_PATH}/assigned-content`;
export const USERS_PATH = `${BASE_PATH}/users`;
export const ANALYTICS_PATH = `${BASE_PATH}/analytics`;
export const ANALYTICS_REPORTS_PATH = `${ANALYTICS_PATH}/reports`;
export const ANALYTICS_DASHBOARDS_PATH = `${ANALYTICS_PATH}/learner-activity`;
export const MESSAGES_PATH = `${BASE_PATH}/messages/list/automated`;
export const MESSAGING_PATH = `${BASE_PATH}/messages`;
export const CUSTOM_COURSES_PATH = `${BASE_PATH}/custom-courses`;
export const ORG_SKILLSETS_PATH = `${BASE_PATH}/skill-sets`;
export const ORG_SKILLS_PATH = `${BASE_PATH}/skills-tracking`;
export const SETTINGS_PATH = `${BASE_PATH}/settings`;
export const ASK_COURSERA_PATH = `${BASE_PATH}/ask-coursera`;

// Organization settings paths
export const ACCOUNT_PATH = `${BASE_PATH}/account`;
export const SSO_CONFIGURE_PATH = `${BASE_PATH}/ssoconfig`;
export const MANAGE_ADMINS_PATH = `${BASE_PATH}/management`;

// Settings side-nav paths (rendered when inside the `${SETTINGS_PATH}` section)
export const SETTINGS_CUSTOMIZE_ORG_PATH = SETTINGS_PATH;
export const SETTINGS_INTEGRITY_GRADING_PATH = `${SETTINGS_PATH}/academic-integrity`;

// Integration settings paths
export const SETTINGS_INTEGRATION_PATH = `${SETTINGS_PATH}/integrate`;
export const SETTINGS_INTEGRATION_API_KEYS_PATH = `${SETTINGS_INTEGRATION_PATH}/api-keys-configuration`;
export const SETTINGS_INTEGRATION_WORKDAY_PATH = `${SETTINGS_INTEGRATION_PATH}/workday-configuration`;
export const SETTINGS_INTEGRATION_DEGREED_PATH = `${SETTINGS_INTEGRATION_PATH}/degreed-configuration`;
export const SETTINGS_INTEGRATION_XAPI_PATH = SETTINGS_INTEGRATION_PATH;
export const SETTINGS_INTEGRATION_MCP_PATH = `${SETTINGS_INTEGRATION_PATH}/mcp`;

// LTI settings paths
export const SETTINGS_LTI_PATH = `${SETTINGS_INTEGRATION_PATH}/lti-tool-registration`;
export const SETTINGS_LTI_TOOL_CONFIGURATIONS_PATH = SETTINGS_LTI_PATH;
export const SETTINGS_LTI_ADDITIONAL_SETTINGS_PATH = `${SETTINGS_LTI_PATH}/additional-settings`;

// SCIM settings paths
export const SETTINGS_SCIM_PATH = `${SETTINGS_PATH}/scim`;
export const SETTINGS_SCIM_SETUP_PATH = SETTINGS_SCIM_PATH;
export const SETTINGS_SCIM_GROUP_MAPPING_PATH = `${SETTINGS_SCIM_PATH}/group-mapping`;

// SSO settings paths
export const SETTINGS_SSO_PATH = `${SETTINGS_PATH}/security`;
// SSO configuration page (the legacy `${BASE_PATH}/ssoconfig` page surfaced in the
// settings side-nav under SSO > Configure SSO).
export const SETTINGS_SSO_SETTINGS_PATH = `${SETTINGS_SSO_PATH}/sso-config`;
export const SETTINGS_SSO_CONFIGURATION_PATH = `${SETTINGS_SSO_PATH}/sso`;

export const SETTINGS_DOMAIN_SECURITY_PATH = `${SETTINGS_SSO_PATH}/domains`;
